export type ResourceCategory =
  | "Integration"
  | "Change Data Capture (CDC)"
  | "Observability"
  | "Enterprise Architecture"
  | "AI & Operational Intelligence"
  | "Integration Delivery Management";

export const resourceCategories: { name: ResourceCategory; description: string }[] = [
  { name: "Integration", description: "Platforms, APIs and architecture patterns for connecting enterprise systems." },
  { name: "Change Data Capture (CDC)", description: "Capturing and moving database changes between systems." },
  { name: "Observability", description: "Understanding the behaviour of complex, distributed environments." },
  { name: "Enterprise Architecture", description: "Mapping and managing applications, data and technology landscapes." },
  { name: "AI & Operational Intelligence", description: "Turning live operational data into insight and decision support." },
  { name: "Integration Delivery Management", description: "Planning, governing and delivering integration work across suppliers, teams and projects." },
];

/** Inline text supports [label](/path) links. */
export type Block =
  | { type: "p"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "diagram"; caption: string; steps: string[] };

export type Article = {
  slug: string;
  title: string;
  seoTitle: string;
  metaDescription: string;
  category: ResourceCategory;
  readingMinutes: number;
  intro: string;
  sections: { heading: string; blocks: Block[] }[];
  related: string[];
  product: { slug: string; name: string; heading: string; text: string };
};

export const resourcePath = (slug: string) => `/resources/${slug}/`;

export const articles: Article[] = [
  {
    slug: "what-is-an-enterprise-integration-platform",
    title: "What Is an Enterprise Integration Platform?",
    seoTitle: "What Is an Enterprise Integration Platform? | Bane Tech",
    metaDescription:
      "Learn what an enterprise integration platform is, its core components, the problems it solves and how it compares with point-to-point integration and ESBs.",
    category: "Integration",
    readingMinutes: 7,
    intro:
      "An enterprise integration platform is a central layer of software that connects applications, databases, devices and external services so they can exchange data and trigger each other's processes reliably. Instead of building and maintaining a separate connection between every pair of systems, organisations define integrations once, on a shared platform that handles transport, transformation, routing, security and monitoring.",
    sections: [
      {
        heading: "The problem it solves",
        blocks: [
          { type: "p", text: "Most organisations run dozens or hundreds of systems: ERP, CRM, billing, data warehouses, industrial equipment, partner APIs and in-house services. Each one holds part of the information the business needs, and each one speaks its own protocol and data format." },
          { type: "p", text: "Connecting them directly, point-to-point, works for the first few integrations. As the number of systems grows, the number of possible connections grows quadratically: 10 systems can require up to 45 individual links, 50 systems up to 1,225. Every link has its own code, credentials, error handling and failure modes, and nobody has a complete picture of how data actually moves." },
          { type: "p", text: "An integration platform replaces that web of custom links with a shared, governed layer." },
        ],
      },
      {
        heading: "Core components",
        blocks: [
          { type: "ul", items: [
            "**Connectivity**: adapters and connectors for protocols such as HTTP/REST, SOAP, message queues, file transfer, databases and industrial or IoT protocols.",
            "**Transformation**: mapping and converting data between formats and schemas (for example, JSON to XML, or one customer model to another).",
            "**Routing and orchestration**: deciding where messages go and coordinating multi-step processes that span several systems.",
            "**API management**: exposing integrations as secured, versioned APIs that internal teams and partners can consume.",
            "**Event handling**: reacting to events as they happen rather than polling on a schedule, the basis of event-driven integration.",
            "**Security**: authentication, authorisation, encryption and secrets management applied consistently across every integration.",
            "**Monitoring**: visibility into throughput, latency, errors and failed messages, so operators can detect and fix problems. See [Observability vs. Monitoring](/resources/observability-vs-monitoring/).",
          ] },
        ],
      },
      {
        heading: "How data flows through an integration platform",
        blocks: [
          { type: "diagram", caption: "A typical request passing through an integration platform", steps: ["Source system", "Connector", "Transform & validate", "Route / orchestrate", "Target systems"] },
          { type: "p", text: "Consider an order placed in an e-commerce application. The platform receives the order event, validates it, converts it to the ERP's format, reserves stock in the warehouse system, notifies the billing service and returns a confirmation, all within a single, observable flow. If the warehouse system is temporarily unavailable, the platform can retry or queue the request instead of losing it." },
        ],
      },
      {
        heading: "Integration platform vs. other approaches",
        blocks: [
          { type: "table", head: ["Approach", "Strengths", "Limitations"], rows: [
            ["Point-to-point", "Quick to build for a single connection", "Becomes hard to maintain and audit as systems multiply"],
            ["Enterprise Service Bus (ESB)", "Centralised mediation and mature tooling", "Often centralised and heavyweight; can become a bottleneck for change"],
            ["Integration platform", "Shared, reusable integrations; APIs and events; cloud and on-premises deployment", "Requires upfront design and platform ownership"],
            ["Custom code in each application", "Full control", "Duplicated effort, inconsistent security and monitoring"],
          ] },
          { type: "p", text: "Modern integration platforms evolved from the ESB model. They typically favour lightweight, containerised deployment, API-first design and event-driven communication. Neither approach is universally superior; the right choice depends on existing investment, scale and latency requirements." },
          { type: "p", text: "Integration platforms also often work alongside [Change Data Capture (CDC)](/resources/what-is-change-data-capture-cdc/), which streams database changes into integration flows without modifying source applications." },
        ],
      },
      {
        heading: "When an integration platform makes sense",
        blocks: [
          { type: "ul", items: [
            "Several systems need to share the same data, and point-to-point links are becoming difficult to maintain.",
            "Processes span multiple applications and need to be coordinated and traced end to end.",
            "Partners or internal teams need secure, consistent API access to business data.",
            "Response times matter, and batch file transfers are no longer fast enough.",
            "Security, auditing and monitoring must be applied uniformly across integrations.",
          ] },
        ],
      },
    ],
    related: ["what-is-change-data-capture-cdc", "observability-vs-monitoring"],
    product: {
      slug: "firefly",
      name: "FireFly",
      heading: "Enterprise Integration with FireFly",
      text: "[FireFly](/products/firefly/) is Bane Tech's integration platform. Discover its capabilities for connecting systems and integrating services across enterprise environments.",
    },
  },
  {
    slug: "what-is-change-data-capture-cdc",
    title: "What Is Change Data Capture (CDC)?",
    seoTitle: "What Is Change Data Capture (CDC)? Methods & Use Cases | Bane Tech",
    metaDescription:
      "Change Data Capture (CDC) explained: how it detects database changes, the main techniques, how it differs from replication and synchronisation, and when to use it.",
    category: "Change Data Capture (CDC)",
    readingMinutes: 8,
    intro:
      "Change Data Capture (CDC) is a set of techniques for identifying the rows that have been inserted, updated or deleted in a database and delivering those changes to other systems. Instead of repeatedly copying entire tables, CDC moves only what changed, usually shortly after it changed, so downstream systems stay current with far less load on the source.",
    sections: [
      {
        heading: "Why CDC exists",
        blocks: [
          { type: "p", text: "Traditional data movement relies on scheduled batch jobs: every night, export a table and load it somewhere else. This approach is simple, but it has three costs. Data is stale between runs, full extracts put heavy load on production databases, and deletions are easy to miss." },
          { type: "p", text: "CDC addresses all three by treating the database as a stream of change events rather than a snapshot." },
        ],
      },
      {
        heading: "How CDC detects changes",
        blocks: [
          { type: "p", text: "There are four common techniques. Each makes a different trade-off between accuracy, latency and impact on the source database." },
          { type: "h3", text: "1. Timestamp or version columns" },
          { type: "p", text: "Queries select rows whose `updated_at` column is newer than the last run. Easy to implement, but it requires a reliable column on every table, cannot detect hard deletes and still polls the database." },
          { type: "h3", text: "2. Database triggers" },
          { type: "p", text: "Triggers write every insert, update and delete to a shadow change table. Changes are captured accurately, including deletes, but each write now performs extra work inside the transaction, which can affect performance on busy systems." },
          { type: "h3", text: "3. Snapshot comparison" },
          { type: "p", text: "Two full copies of a table are compared to compute differences. It works on any source but is expensive and only suitable for small tables or infrequent runs." },
          { type: "h3", text: "4. Transaction log reading" },
          { type: "p", text: "Most relational databases record every committed change in a transaction log (for example, the PostgreSQL WAL, the MySQL binlog or the SQL Server transaction log). Log-based CDC reads this log and converts entries into change events. It captures all changes in commit order with minimal impact on the application, but depends on database-specific log access and configuration." },
          { type: "table", head: ["Technique", "Captures deletes", "Source impact", "Latency"], rows: [
            ["Timestamp columns", "No", "Medium (polling)", "Minutes"],
            ["Triggers", "Yes", "Higher (extra writes)", "Seconds"],
            ["Snapshot comparison", "Yes", "High", "Hours"],
            ["Transaction log", "Yes", "Low", "Sub-second to seconds"],
          ] },
        ],
      },
      {
        heading: "Anatomy of a CDC pipeline",
        blocks: [
          { type: "diagram", caption: "A simplified CDC pipeline", steps: ["Source database", "Change capture", "Change events", "Transform / filter", "Targets: DW, cache, services"] },
          { type: "p", text: "A change event usually contains the operation type (insert, update, delete), the table, the primary key, the new values and often the previous values, plus a position marker so the pipeline can resume exactly where it stopped after a restart." },
        ],
      },
      {
        heading: "CDC vs. replication vs. synchronisation",
        blocks: [
          { type: "p", text: "These terms overlap and are often used interchangeably, but they describe different things:" },
          { type: "ul", items: [
            "**Database replication**: maintains a copy of a database, usually of the same engine, for availability or read scaling. It is typically managed by the database itself.",
            "**CDC**: captures changes as events that can be delivered to any kind of target: a different database, a data warehouse, a message broker, a search index or an application.",
            "**Data synchronisation**: is the broader goal of keeping two or more systems consistent. CDC is one of the mechanisms used to achieve it.",
          ] },
        ],
      },
      {
        heading: "Common use cases",
        blocks: [
          { type: "ul", items: [
            "Feeding data warehouses and analytics platforms without nightly batch windows.",
            "Keeping caches and search indexes consistent with the system of record.",
            "Migrating databases with minimal downtime by streaming changes during cut-over.",
            "Publishing business events (for example, “order created”) from legacy applications that cannot be modified.",
            "Triggering processes in an [enterprise integration platform](/resources/what-is-an-enterprise-integration-platform/) when data changes.",
          ] },
        ],
      },
      {
        heading: "Things to plan for",
        blocks: [
          { type: "ul", items: [
            "**Schema changes**: how the pipeline handles added, removed or renamed columns.",
            "**Ordering and delivery guarantees**: whether consumers can tolerate duplicates or out-of-order events.",
            "**Initial load**: most pipelines need a consistent snapshot before streaming begins.",
            "**Monitoring**: lag between source and target is the key health indicator. See [Observability vs. Monitoring](/resources/observability-vs-monitoring/).",
          ] },
        ],
      },
    ],
    related: ["what-is-an-enterprise-integration-platform", "observability-vs-monitoring"],
    product: {
      slug: "fireant",
      name: "FireAnt",
      heading: "Implementing Change Data Capture",
      text: "[FireAnt](/products/fireant/) is Bane Tech's Change Data Capture product. Explore how it supports change data capture and data movement across your environment.",
    },
  },
  {
    slug: "observability-vs-monitoring",
    title: "Observability vs. Monitoring: What Is the Difference?",
    seoTitle: "Observability vs. Monitoring: Key Differences Explained | Bane Tech",
    metaDescription:
      "Monitoring tells you that something is wrong; observability helps you understand why. Learn the difference with practical examples from distributed systems.",
    category: "Observability",
    readingMinutes: 6,
    intro:
      "Monitoring tells you when something you anticipated has gone wrong: a server is down, an error rate has crossed a threshold. Observability is the ability to understand why a system is behaving the way it is, including in situations nobody anticipated, by exploring the data it emits. Monitoring is something you do; observability is a property of the system and its telemetry.",
    sections: [
      {
        heading: "Monitoring: known questions",
        blocks: [
          { type: "p", text: "Monitoring is built around predefined checks: CPU usage, disk space, HTTP status codes, queue depth, response time. Each check answers a question you knew to ask in advance, and an alert fires when a value leaves its expected range." },
          { type: "p", text: "This works well for predictable failure modes. A database server running out of disk space, a certificate about to expire or a service that stops responding are all caught reliably by monitoring." },
        ],
      },
      {
        heading: "Observability: unknown questions",
        blocks: [
          { type: "p", text: "Distributed systems fail in less predictable ways. A request might pass through an API gateway, an integration layer, three microservices, a cache and two databases. When users report that checkout is slow for some customers, no single dashboard shows the cause." },
          { type: "p", text: "Observability relies on rich telemetry that can be sliced and correlated after the fact:" },
          { type: "ul", items: [
            "**Metrics**: numeric measurements over time (latency, throughput, error rates).",
            "**Logs**: timestamped records of discrete events, ideally structured.",
            "**Traces**: the end-to-end path of a single request across services, with timing for each step.",
            "**Context**: attributes such as customer, region, version or tenant that let you filter and group.",
          ] },
        ],
      },
      {
        heading: "A practical example",
        blocks: [
          { type: "diagram", caption: "One request crossing several services", steps: ["Web app", "API gateway", "Order service", "Pricing service", "Database"] },
          { type: "p", text: "Monitoring shows that average checkout latency has risen from 300 ms to 900 ms. All servers are healthy and no alerts have fired on individual components." },
          { type: "p", text: "With observability, an engineer filters traces for slow checkouts and notices they all involve one pricing region. Within those traces, a single database query has grown from 20 ms to 600 ms. Correlated logs show a recent deployment changed that query. The cause is found by exploring data, not by an alert that someone configured in advance." },
        ],
      },
      {
        heading: "Side-by-side comparison",
        blocks: [
          { type: "table", head: ["", "Monitoring", "Observability"], rows: [
            ["Question answered", "Is something wrong?", "Why is it wrong?"],
            ["Approach", "Predefined checks and thresholds", "Exploring correlated telemetry"],
            ["Best for", "Known failure modes", "Novel or complex failures"],
            ["Typical data", "Metrics, health checks", "Metrics, logs, traces, context"],
            ["Output", "Alerts and dashboards", "Diagnosis and root cause"],
          ] },
        ],
      },
      {
        heading: "You need both",
        blocks: [
          { type: "p", text: "Observability does not replace monitoring. Monitoring provides fast, reliable alerting on what matters to the business; observability makes the investigation that follows faster. Mature teams monitor service-level objectives and use observability data to diagnose deviations." },
          { type: "p", text: "Integration-heavy environments benefit especially, because a single business transaction often crosses many systems. See [What Is an Enterprise Integration Platform?](/resources/what-is-an-enterprise-integration-platform/) and, for data pipelines, [What Is Change Data Capture (CDC)?](/resources/what-is-change-data-capture-cdc/)." },
        ],
      },
    ],
    related: ["what-is-an-enterprise-integration-platform", "what-is-change-data-capture-cdc"],
    product: {
      slug: "lemur-monitor",
      name: "Lemur Monitor",
      heading: "Enterprise Observability with Lemur Monitor",
      text: "Explore [Lemur Monitor](/products/lemur-monitor/) to learn how Bane Tech approaches monitoring and observability in complex environments.",
    },
  },
  {
    slug: "enterprise-integration-platform-vs-esb",
    title: "Enterprise Integration Platform vs. ESB",
    seoTitle: "Enterprise Integration Platform vs. ESB: Key Differences | Bane Tech",
    metaDescription:
      "Compare modern enterprise integration platforms with the Enterprise Service Bus (ESB): architecture, scalability, deployment, governance and when to migrate.",
    category: "Integration",
    readingMinutes: 6,
    intro:
      "The Enterprise Service Bus (ESB) was the dominant integration architecture of the 2000s. Modern enterprise integration platforms solve the same core problem, connecting heterogeneous systems, but with different assumptions about scale, deployment and ownership. Understanding the differences helps architects decide whether to keep, extend or replace an existing ESB.",
    sections: [
      {
        heading: "What an ESB is",
        blocks: [
          { type: "p", text: "An ESB is a central middleware layer through which services communicate. It provides message routing, protocol mediation, transformation and often orchestration, typically deployed as a single, centrally managed runtime owned by an integration team." },
          { type: "p", text: "ESBs brought order to point-to-point chaos and introduced reusable services. Their weaknesses emerged as organisations moved to cloud, containers and continuous delivery: a single shared runtime becomes a bottleneck for change, scaling is coarse-grained, and business logic tends to accumulate inside the bus." },
        ],
      },
      {
        heading: "What changes with a modern integration platform",
        blocks: [
          { type: "ul", items: [
            "**Distributed runtime**: integrations run as independently deployable, horizontally scalable units rather than inside one monolithic bus.",
            "**API-first**: integrations are exposed and consumed as versioned, secured APIs.",
            "**Event-driven**: systems react to events as they occur instead of relying on synchronous calls or scheduled batches. See [Event-Driven Integration Architecture](/resources/event-driven-integration-architecture/).",
            "**Built-in observability**: latency, throughput and failures are visible per flow, not only per server.",
            "**Deployment flexibility**: on-premises, cloud or hybrid, often containerised.",
          ] },
        ],
      },
      {
        heading: "Side-by-side comparison",
        blocks: [
          { type: "table", head: ["Dimension", "Traditional ESB", "Modern integration platform"], rows: [
            ["Topology", "Centralised bus", "Distributed, independently scalable flows"],
            ["Scaling", "Scale the whole runtime", "Scale individual integrations"],
            ["Change cadence", "Coordinated releases", "Independent, continuous deployment"],
            ["Interaction style", "Mostly request/response, SOAP", "APIs, events and streams"],
            ["Logic placement", "Often inside the bus", "Kept thin; business logic stays in services"],
            ["Visibility", "Server-level monitoring", "Per-flow metrics and tracing"],
          ] },
        ],
      },
      {
        heading: "When to migrate from an ESB",
        blocks: [
          { type: "p", text: "Migration rarely needs to be a big-bang replacement. A common approach is to route new integrations through the new platform while progressively moving existing ESB flows, starting with those that change most often or suffer the most from performance limits." },
          { type: "ul", items: [
            "Release cycles are blocked by the shared integration runtime.",
            "Latency or throughput requirements exceed what the bus can deliver.",
            "Licensing or infrastructure costs grow faster than integration value.",
            "Operators lack visibility into individual flows.",
          ] },
        ],
      },
    ],
    related: ["what-is-an-enterprise-integration-platform", "api-orchestration-explained"],
    product: {
      slug: "firefly",
      name: "FireFly",
      heading: "Modern Integration with FireFly",
      text: "[FireFly](/products/firefly/) is Bane Tech's integration platform. Learn how it approaches connecting systems and services in enterprise environments.",
    },
  },
  {
    slug: "api-orchestration-explained",
    title: "API Orchestration Explained",
    seoTitle: "API Orchestration Explained: Patterns & Best Practices | Bane Tech",
    metaDescription:
      "What API orchestration is, how it differs from choreography and API aggregation, common patterns, error handling and when to use an integration platform.",
    category: "Integration",
    readingMinutes: 6,
    intro:
      "API orchestration is the coordination of multiple API calls into a single, managed process. A central orchestrator decides which services to call, in what order, how to combine their results and what to do when one of them fails, so consumers interact with one well-defined operation instead of a sequence of low-level calls.",
    sections: [
      {
        heading: "Why orchestration is needed",
        blocks: [
          { type: "p", text: "Business operations rarely map to a single API. Onboarding a customer may involve identity verification, CRM record creation, credit checks, account provisioning and notifications. Letting every client implement that sequence duplicates logic, spreads error handling across applications and makes changes risky." },
          { type: "p", text: "An orchestration layer centralises the sequence, applies consistent security and retries, and exposes the result as one API." },
        ],
      },
      {
        heading: "How an orchestrated flow works",
        blocks: [
          { type: "diagram", caption: "A single client request orchestrated across several services", steps: ["Client request", "Orchestrator", "Service A + Service B", "Combine & validate", "Single response"] },
          { type: "p", text: "Independent calls can run in parallel to reduce total latency; dependent calls run in sequence. The orchestrator tracks state so it can retry, time out or compensate when a step fails." },
        ],
      },
      {
        heading: "Orchestration vs. choreography",
        blocks: [
          { type: "table", head: ["", "Orchestration", "Choreography"], rows: [
            ["Control", "Central coordinator", "Each service reacts to events"],
            ["Visibility", "Whole process visible in one place", "Process emerges from events; harder to trace"],
            ["Coupling", "Services coupled to the orchestrator", "Services coupled to event contracts"],
            ["Best for", "Well-defined, multi-step business transactions", "Loosely coupled, highly scalable reactions"],
          ] },
          { type: "p", text: "Many architectures combine both: orchestration for transactional processes and choreography for event propagation. See [Event-Driven Integration Architecture](/resources/event-driven-integration-architecture/)." },
        ],
      },
      {
        heading: "Best practices",
        blocks: [
          { type: "ul", items: [
            "Keep orchestration logic about coordination, not core business rules.",
            "Design idempotent steps so retries are safe.",
            "Use compensating actions (the saga pattern) instead of distributed transactions.",
            "Set explicit timeouts and circuit breakers for every downstream call.",
            "Trace each request end-to-end with a correlation ID. See [What Is Enterprise Observability?](/resources/what-is-enterprise-observability/).",
          ] },
        ],
      },
    ],
    related: ["enterprise-integration-platform-vs-esb", "event-driven-integration-architecture"],
    product: {
      slug: "firefly",
      name: "FireFly",
      heading: "API Orchestration with FireFly",
      text: "Explore [FireFly](/products/firefly/) to see how Bane Tech's integration platform exposes and coordinates APIs across enterprise systems.",
    },
  },
  {
    slug: "event-driven-integration-architecture",
    title: "Event-Driven Integration Architecture",
    seoTitle: "Event-Driven Integration Architecture Explained | Bane Tech",
    metaDescription:
      "Learn how event-driven integration architecture works: events, producers, consumers, brokers, delivery guarantees and how it compares with request/response integration.",
    category: "Integration",
    readingMinutes: 7,
    intro:
      "In an event-driven integration architecture, systems communicate by publishing and reacting to events, which record something that has happened, such as an order being placed or an invoice being paid. Producers don't need to know who consumes their events, keeping systems loosely coupled and enabling data to flow as soon as changes occur.",
    sections: [
      {
        heading: "Core concepts",
        blocks: [
          { type: "ul", items: [
            "**Event**: an immutable fact with a type, payload and timestamp.",
            "**Producer**: the system that publishes the event.",
            "**Broker or event channel**: the infrastructure that stores and distributes events.",
            "**Consumer**: any system that subscribes and reacts.",
            "**Event schema**: the contract describing the event's structure, ideally versioned.",
          ] },
        ],
      },
      {
        heading: "How it works",
        blocks: [
          { type: "diagram", caption: "One event, many independent consumers", steps: ["Producer", "Event published", "Broker / channel", "Consumers react"] },
          { type: "p", text: "When a customer changes their address, the CRM publishes a single event. Billing, logistics and analytics each consume it independently. Adding a new consumer requires no change to the CRM." },
          { type: "p", text: "Events can originate from applications or directly from databases using [Change Data Capture (CDC)](/resources/what-is-change-data-capture-cdc/), which turns committed data changes into event streams without modifying the source application." },
        ],
      },
      {
        heading: "Event-driven vs. request/response",
        blocks: [
          { type: "table", head: ["", "Request/response", "Event-driven"], rows: [
            ["Coupling", "Caller must know and wait for the callee", "Producer unaware of consumers"],
            ["Timing", "Synchronous", "Asynchronous, near real time"],
            ["Failure impact", "Callee outage blocks caller", "Events buffered until consumers recover"],
            ["Typical use", "Queries, commands needing an immediate answer", "Propagating state changes, notifications, streams"],
          ] },
        ],
      },
      {
        heading: "Design considerations",
        blocks: [
          { type: "ul", items: [
            "**Delivery guarantees**: at-most-once, at-least-once or effectively-once; consumers should be idempotent.",
            "**Ordering**: guarantee order where it matters, typically per entity key.",
            "**Schema evolution**: add fields compatibly and version breaking changes.",
            "**Replay**: retaining events allows new consumers to rebuild state.",
            "**Observability**: monitor lag, throughput and dead-letter queues.",
          ] },
        ],
      },
    ],
    related: ["api-orchestration-explained", "real-time-data-synchronization-explained"],
    product: {
      slug: "firefly",
      name: "FireFly",
      heading: "Event-Driven Integration with FireFly",
      text: "Discover how [FireFly](/products/firefly/), Bane Tech's integration platform, supports connecting systems and services in enterprise environments.",
    },
  },
  {
    slug: "log-based-cdc-explained",
    title: "Log-Based CDC Explained",
    seoTitle: "Log-Based Change Data Capture (CDC) Explained | Bane Tech",
    metaDescription:
      "How log-based change data capture reads database transaction logs to stream inserts, updates and deletes with low overhead, and how it compares with trigger and query-based CDC.",
    category: "Change Data Capture (CDC)",
    readingMinutes: 6,
    intro:
      "Log-based change data capture reads a database's transaction log (the record the database itself keeps of every committed change) and converts those entries into a stream of change events. Because it reads the log rather than querying tables, it captures every insert, update and delete with minimal impact on the source system.",
    sections: [
      {
        heading: "Where the changes come from",
        blocks: [
          { type: "p", text: "Relational databases write each change to a log before applying it, for durability and recovery. Different engines name it differently: the write-ahead log (WAL) in PostgreSQL, the binary log in MySQL, the redo log in Oracle and the transaction log in SQL Server. A log-based CDC process reads these entries in commit order." },
          { type: "diagram", caption: "Log-based CDC pipeline", steps: ["Application writes", "Transaction log", "CDC reader", "Change events", "Target systems"] },
        ],
      },
      {
        heading: "Comparison with other CDC methods",
        blocks: [
          { type: "table", head: ["Method", "How it works", "Trade-offs"], rows: [
            ["Query-based", "Periodically selects rows changed since a timestamp", "Misses deletes and intermediate changes; adds query load"],
            ["Trigger-based", "Database triggers write changes to shadow tables", "Captures everything but adds overhead to every write"],
            ["Log-based", "Reads the transaction log", "Low overhead, complete and ordered; requires log access and configuration"],
          ] },
        ],
      },
      {
        heading: "Advantages",
        blocks: [
          { type: "ul", items: [
            "Captures all changes, including deletes, in commit order.",
            "No changes to application code or schema.",
            "Low latency between commit and event.",
            "Low additional load on the source database.",
          ] },
        ],
      },
      {
        heading: "Operational considerations",
        blocks: [
          { type: "ul", items: [
            "Log retention must exceed the longest expected consumer downtime.",
            "An initial snapshot is usually needed before streaming begins.",
            "Schema changes in the source must be detected and propagated.",
            "Required permissions and log settings differ per database engine.",
            "Replication lag should be monitored continuously.",
          ] },
          { type: "p", text: "For background on CDC in general, see [What Is Change Data Capture (CDC)?](/resources/what-is-change-data-capture-cdc/)." },
        ],
      },
    ],
    related: ["what-is-change-data-capture-cdc", "cdc-vs-database-replication"],
    product: {
      slug: "fireant",
      name: "FireAnt",
      heading: "Change Data Capture with FireAnt",
      text: "Learn about [FireAnt](/products/fireant/), Bane Tech's change data capture product, and how it approaches moving data changes between systems.",
    },
  },
  {
    slug: "cdc-vs-database-replication",
    title: "CDC vs. Database Replication",
    seoTitle: "CDC vs. Database Replication: Differences & Use Cases | Bane Tech",
    metaDescription:
      "Change data capture vs. database replication: how they work, key differences in purpose, targets and flexibility, and when to choose each.",
    category: "Change Data Capture (CDC)",
    readingMinutes: 5,
    intro:
      "Database replication and change data capture both move data changes from one place to another, and often rely on the same transaction logs. Their purpose is different: replication keeps copies of a database identical, while CDC turns changes into events that any system can consume, transform and act on.",
    sections: [
      {
        heading: "Database replication",
        blocks: [
          { type: "p", text: "Replication maintains one or more copies of a database, usually on the same engine. It serves high availability, disaster recovery and read scaling. The replica mirrors the source structure and is typically managed by the database vendor's own tooling." },
        ],
      },
      {
        heading: "Change data capture",
        blocks: [
          { type: "p", text: "CDC captures individual inserts, updates and deletes and publishes them as a stream. Targets can be different databases, data warehouses, search indexes, caches, message brokers or applications, and data can be filtered or transformed on the way." },
        ],
      },
      {
        heading: "Key differences",
        blocks: [
          { type: "table", head: ["", "Replication", "CDC"], rows: [
            ["Primary goal", "Identical copy of the database", "Propagate changes to other systems"],
            ["Targets", "Usually same engine", "Heterogeneous: databases, streams, apps"],
            ["Transformation", "None or minimal", "Filtering, mapping, enrichment"],
            ["Granularity", "Entire database or tables", "Selected tables, columns or rows"],
            ["Typical uses", "HA, disaster recovery, read replicas", "Integration, analytics, event-driven architecture, migrations"],
          ] },
        ],
      },
      {
        heading: "Which one to choose",
        blocks: [
          { type: "ul", items: [
            "Choose **replication** when you need a failover copy or read scaling on the same database engine.",
            "Choose **CDC** when changes must reach different systems, formats or teams.",
            "Use **both** where needed: replication for resilience, CDC for integration.",
          ] },
          { type: "p", text: "See [Log-Based CDC Explained](/resources/log-based-cdc-explained/) for how CDC reads changes efficiently." },
        ],
      },
    ],
    related: ["log-based-cdc-explained", "real-time-data-synchronization-explained"],
    product: {
      slug: "fireant",
      name: "FireAnt",
      heading: "Moving Data Changes with FireAnt",
      text: "Explore [FireAnt](/products/fireant/) to learn how Bane Tech approaches change data capture across enterprise systems.",
    },
  },
  {
    slug: "real-time-data-synchronization-explained",
    title: "Real-Time Data Synchronization Explained",
    seoTitle: "Real-Time Data Synchronization Explained | Bane Tech",
    metaDescription:
      "What real-time data synchronization is, the techniques behind it (CDC, events, APIs), consistency and conflict handling, and how to design reliable sync between systems.",
    category: "Change Data Capture (CDC)",
    readingMinutes: 6,
    intro:
      "Real-time data synchronization keeps data consistent across multiple systems as it changes, rather than in nightly or hourly batches. When a record is updated in one system, the change reaches every other system that depends on it within seconds or less.",
    sections: [
      {
        heading: "Why batch is no longer enough",
        blocks: [
          { type: "p", text: "Batch jobs leave systems out of step for hours. Customers see outdated balances, stock levels drift between channels and analytics trail operations. As processes become digital and continuous, the window of inconsistency becomes a business risk." },
        ],
      },
      {
        heading: "Common techniques",
        blocks: [
          { type: "ul", items: [
            "**Change data capture**: stream committed database changes. See [What Is Change Data Capture (CDC)?](/resources/what-is-change-data-capture-cdc/).",
            "**Application events**: services publish domain events. See [Event-Driven Integration Architecture](/resources/event-driven-integration-architecture/).",
            "**API calls and webhooks**: push changes directly to dependent systems.",
          ] },
          { type: "diagram", caption: "One-way real-time synchronization", steps: ["Source change", "Capture", "Transform & route", "Apply to targets", "Confirm & monitor"] },
        ],
      },
      {
        heading: "Consistency and conflicts",
        blocks: [
          { type: "p", text: "Most distributed synchronization is eventually consistent: targets converge shortly after the source. Designs should define a system of record for each entity, preserve ordering per key and make writes idempotent." },
          { type: "p", text: "Bidirectional synchronization adds conflict resolution (for example, last-writer-wins, field-level ownership or explicit merge rules) and loop prevention so a change does not echo back to its origin." },
        ],
      },
      {
        heading: "Making synchronization reliable",
        blocks: [
          { type: "ul", items: [
            "Buffer changes so target outages do not lose data.",
            "Track lag and failed records per target.",
            "Support re-synchronization from a snapshot.",
            "Version schemas and validate payloads.",
            "Reconcile periodically to detect drift.",
          ] },
        ],
      },
    ],
    related: ["cdc-vs-database-replication", "log-based-cdc-explained"],
    product: {
      slug: "fireant",
      name: "FireAnt",
      heading: "Data Synchronization with FireAnt",
      text: "Learn how [FireAnt](/products/fireant/), Bane Tech's change data capture product, helps keep data moving between systems.",
    },
  },
  {
    slug: "what-is-enterprise-observability",
    title: "What Is Enterprise Observability?",
    seoTitle: "What Is Enterprise Observability? A Practical Guide | Bane Tech",
    metaDescription:
      "Enterprise observability explained: metrics, logs and traces at organisational scale, how it differs from monitoring, key capabilities and how to get started.",
    category: "Observability",
    readingMinutes: 6,
    intro:
      "Enterprise observability is the ability to understand the internal state of an organisation's entire technology landscape (applications, infrastructure, integrations and data flows) from the telemetry it produces. It extends observability practices from individual services to the scale, diversity and governance requirements of a large enterprise.",
    sections: [
      {
        heading: "The telemetry foundation",
        blocks: [
          { type: "ul", items: [
            "**Metrics**: numeric measurements over time, such as latency, throughput and error rates.",
            "**Logs**: timestamped records of discrete events.",
            "**Traces**: the path of a single request across services.",
            "**Events and topology**: deployments, configuration changes and dependencies between components.",
          ] },
        ],
      },
      {
        heading: "What makes it \"enterprise\"",
        blocks: [
          { type: "p", text: "Enterprises run a mix of modern and legacy systems, on-premises and cloud, owned by many teams. Enterprise observability must correlate telemetry across all of them, map technical signals to business services and respect security, retention and access policies." },
          { type: "diagram", caption: "From raw telemetry to decisions", steps: ["Collect telemetry", "Correlate & enrich", "Map to services", "Detect & alert", "Analyse & act"] },
        ],
      },
      {
        heading: "Key capabilities",
        blocks: [
          { type: "ul", items: [
            "Unified collection across heterogeneous systems.",
            "Service and dependency maps linking components to business functions.",
            "SLOs and SLA tracking per business service.",
            "Anomaly detection and alerting with low noise.",
            "End-to-end tracing across integrations and data pipelines.",
            "Role-based access and governed data retention.",
          ] },
          { type: "p", text: "For the distinction between watching known signals and investigating unknown problems, see [Observability vs. Monitoring](/resources/observability-vs-monitoring/)." },
        ],
      },
      {
        heading: "Getting started",
        blocks: [
          { type: "ul", items: [
            "Identify the business services that matter most and define their SLOs.",
            "Standardise telemetry formats and correlation IDs.",
            "Instrument integrations and data flows, not only applications.",
            "Consolidate alerts around user impact.",
            "Review incidents to close visibility gaps.",
          ] },
        ],
      },
    ],
    related: ["observability-vs-monitoring", "what-is-an-enterprise-integration-platform"],
    product: {
      slug: "lemur-monitor",
      name: "Lemur Monitor",
      heading: "Enterprise Observability with Lemur Monitor",
      text: "Explore [Lemur Monitor](/products/lemur-monitor/) to learn how Bane Tech approaches monitoring and observability in complex environments.",
    },
  },
  {
    slug: "what-is-enterprise-architecture-management",
    title: "What Is Enterprise Architecture Management?",
    seoTitle: "What Is Enterprise Architecture Management (EAM)? | Bane Tech",
    metaDescription:
      "Enterprise Architecture Management (EAM) explained: what it covers, the core building blocks, why static diagrams fall short and how a live architecture model supports decisions.",
    category: "Enterprise Architecture",
    readingMinutes: 7,
    intro:
      "Enterprise Architecture Management (EAM) is the practice of documenting, analysing and planning how an organisation's business capabilities, applications, data and infrastructure fit together. Its purpose is to give decision-makers an accurate picture of the current landscape, so that investments, modernisation and risk can be managed deliberately rather than by accident.",
    sections: [
      {
        heading: "What enterprise architecture covers",
        blocks: [
          { type: "ul", items: [
            "**Business architecture**: capabilities, processes and the organisational units that own them.",
            "**Application architecture**: the portfolio of applications, their purpose, lifecycle and owners.",
            "**Data architecture**: the main information objects and which systems create, store and consume them.",
            "**Technology architecture**: platforms, infrastructure and the technical standards in use.",
            "**Integration architecture**: how systems exchange data. See [What Is an Enterprise Integration Platform?](/resources/what-is-an-enterprise-integration-platform/).",
          ] },
        ],
      },
      {
        heading: "Why static architecture documentation falls short",
        blocks: [
          { type: "p", text: "Many architecture repositories are maintained by hand: spreadsheets, diagrams and wiki pages updated during projects or audits. The landscape, however, changes every week. Applications are deployed, integrations added and interfaces retired, and the documentation drifts away from reality." },
          { type: "p", text: "Decisions based on an outdated model carry risk: an application considered unused may still feed a critical report, and a planned migration may miss dependencies nobody recorded." },
        ],
      },
      {
        heading: "From a static repository to a live model",
        blocks: [
          { type: "diagram", caption: "Keeping an architecture model aligned with the running landscape", steps: ["Operational sources", "Discovery & import", "Architecture model", "Analysis & roadmaps", "Decisions"] },
          { type: "p", text: "A live approach enriches the architecture model with information from systems that already know the truth, including integration platforms, monitoring tools, CMDBs and deployment pipelines. Architects still curate the model, but they spend less time collecting data and more time analysing it." },
          { type: "p", text: "Operational signals from [enterprise observability](/resources/what-is-enterprise-observability/) can, for example, reveal which interfaces are actually used and how critical they are." },
        ],
      },
      {
        heading: "Typical use cases",
        blocks: [
          { type: "table", head: ["Use case", "Question answered"], rows: [
            ["Application portfolio rationalisation", "Which applications overlap, and which can be retired?"],
            ["Impact analysis", "What is affected if this system or interface changes?"],
            ["Modernisation roadmaps", "In which order should systems be migrated?"],
            ["Risk and compliance", "Which applications process sensitive data, and who owns them?"],
          ] },
        ],
      },
    ],
    related: ["what-is-an-enterprise-integration-platform", "what-is-enterprise-observability"],
    product: {
      slug: "bee-portfolio",
      name: "Bee Portfolio",
      heading: "Live Enterprise Architecture with Bee Portfolio",
      text: "[Bee Portfolio](/products/bee-portfolio/) is Bane Tech's enterprise architecture product. Explore how it supports mapping and managing your application landscape.",
    },
  },
  {
    slug: "what-is-ai-operational-intelligence",
    title: "What Is AI-Driven Operational Intelligence?",
    seoTitle: "What Is AI-Driven Operational Intelligence? | Bane Tech",
    metaDescription:
      "AI-driven operational intelligence explained: how machine learning turns real-time operational data into anomaly detection, correlation and decision support.",
    category: "AI & Operational Intelligence",
    readingMinutes: 7,
    intro:
      "AI-driven operational intelligence is the use of machine learning and analytics on live operational data, including metrics, events, transactions and logs, to detect what is happening in a business or technical environment, explain why, and support decisions while they still matter. It sits between traditional dashboards, which show data, and automation, which acts on it.",
    sections: [
      {
        heading: "From data to decisions",
        blocks: [
          { type: "diagram", caption: "The operational intelligence loop", steps: ["Live data streams", "Correlation & features", "AI models", "Insights & alerts", "Decisions / actions"] },
          { type: "p", text: "The value comes from speed and context. A batch report can tell you yesterday's failure rate; operational intelligence tries to tell you that the failure rate is deviating now, which entities are involved and what has changed." },
        ],
      },
      {
        heading: "Core capabilities",
        blocks: [
          { type: "ul", items: [
            "**Anomaly detection**: learning normal behaviour and flagging deviations instead of relying only on fixed thresholds.",
            "**Correlation**: linking signals across systems so related symptoms are grouped into a single situation.",
            "**Entity modelling**: representing customers, devices, services or assets so data can be analysed in business terms.",
            "**Forecasting**: anticipating load, demand or degradation before it happens.",
            "**Decision support**: ranking likely causes or recommended actions for a human operator.",
          ] },
        ],
      },
      {
        heading: "Where the data comes from",
        blocks: [
          { type: "p", text: "Operational intelligence depends on timely, trustworthy data. Typical sources are [observability platforms](/resources/what-is-enterprise-observability/), integration flows and database change streams delivered through [Change Data Capture (CDC)](/resources/what-is-change-data-capture-cdc/)." },
          { type: "p", text: "Model quality is limited by data quality: incomplete, delayed or inconsistent data produces unreliable insights." },
        ],
      },
      {
        heading: "Practical considerations",
        blocks: [
          { type: "table", head: ["Consideration", "Why it matters"], rows: [
            ["Explainability", "Operators need to understand why an alert or recommendation was produced."],
            ["Human oversight", "High-impact decisions should be reviewed before automation is trusted."],
            ["Feedback loops", "Models improve when operators confirm or reject findings."],
            ["Latency", "Insights must arrive while action is still possible."],
          ] },
        ],
      },
    ],
    related: ["what-is-enterprise-observability", "what-is-change-data-capture-cdc"],
    product: {
      slug: "gaia-entity",
      name: "Gaia Entity",
      heading: "Operational Intelligence with Gaia Entity",
      text: "[Gaia Entity](/products/gaia-entity/) is Bane Tech's polymorphic AI product. Explore how it approaches real-time analysis and decision support.",
    },
  },
  {
    slug: "what-is-developer-access-management",
    title: "What Is Developer Access Management for Integration Projects?",
    seoTitle: "Developer Access Management for Integration Projects | Bane Tech",
    metaDescription:
      "How organisations govern which developers can access services, documentation and environments in integration projects, and why least-privilege access matters.",
    category: "Integration Delivery Management",
    readingMinutes: 6,
    intro:
      "Developer access management is the practice of controlling which developers, teams and suppliers can see and use specific services, APIs, documentation and environments during a project. In integration work it ensures that people get exactly what their project requires, no more, while team leaders retain visibility over who is working on what.",
    sections: [
      {
        heading: "Why it matters in integration projects",
        blocks: [
          { type: "p", text: "Integration projects rarely involve a single team: internal teams, suppliers and contractors all work against the same shared platform. Without structure, access is granted ad hoc, credentials are shared and it becomes unclear who can call which service." },
          { type: "ul", items: [
            "**Security**: least-privilege access reduces the impact of leaked credentials or mistakes.",
            "**Focus**: developers see the services and documentation relevant to their project.",
            "**Accountability**: team leaders know who requested and used which resources.",
            "**Onboarding and offboarding**: access follows project membership instead of manual tickets.",
          ] },
        ],
      },
      {
        heading: "A project-based access model",
        blocks: [
          { type: "diagram", caption: "Access granted through project membership", steps: ["Project", "Team & roles", "Assigned services & docs", "Environments", "Usage visibility"] },
          { type: "p", text: "Instead of assigning permissions to individuals one by one, access is attached to a project. Members inherit the services, documentation and environments assigned to that project, and lose them when they leave it." },
          { type: "p", text: "This model works best when the services themselves are exposed through a governed layer such as an [enterprise integration platform](/resources/what-is-an-enterprise-integration-platform/) with consistent [API orchestration](/resources/api-orchestration-explained/)." },
        ],
      },
      {
        heading: "Good practices",
        blocks: [
          { type: "table", head: ["Practice", "Benefit"], rows: [
            ["Least privilege by default", "Limits exposure if something goes wrong"],
            ["Role-based team leader controls", "Delegates administration without central bottlenecks"],
            ["Per-project documentation", "Developers find the right contracts quickly"],
            ["Request visibility", "Usage can be traced back to projects and teams"],
            ["Regular access reviews", "Removes stale permissions"],
          ] },
        ],
      },
    ],
    related: ["what-is-integration-delivery-management", "what-is-an-enterprise-integration-platform", "api-orchestration-explained"],
    product: {
      slug: "koala-flow",
      name: "Koala Flow",
      heading: "Delivery and Access Management with Koala Flow",
      text: "[Koala Flow](/products/koala-flow/) is Bane Tech's enterprise project & integration delivery management product, covering programs, projects, suppliers and teams. Explore how it organises delivery and developer access.",
    },
  },
  {
    slug: "what-is-integration-delivery-management",
    title: "What Is Enterprise Project & Integration Delivery Management?",
    seoTitle: "Enterprise Project & Integration Delivery Management Explained | Bane Tech",
    metaDescription:
      "How organisations plan, govern and deliver integration work across suppliers, teams and projects, with a unified view of ownership, access and integration progress.",
    category: "Integration Delivery Management",
    readingMinutes: 6,
    intro:
      "Enterprise project & integration delivery management brings integration initiatives together under a unified governance framework, coordinating the teams, suppliers and contractors responsible for connecting systems, services and data across the enterprise. It answers four questions continuously: what is being delivered, by whom, with what access, and how far along each integration actually is.",
    sections: [
      {
        heading: "Why integration delivery is harder than ordinary project management",
        blocks: [
          { type: "p", text: "Integration programs rarely involve one team. A typical program brings together an internal platform team, one or more suppliers building services against that platform, and business owners whose systems are being connected. The work is interdependent: one supplier cannot finish until an interface another supplier publishes is ready." },
          { type: "ul", items: [
            "**Shared platform**: several suppliers build against the same services, documentation and environments.",
            "**Interdependent work**: a change to one interface affects every team consuming it.",
            "**Distributed accountability**: a single business flow often spans more than one organisation.",
            "**Visibility gaps**: progress is reported in status meetings instead of read from the platform itself.",
          ] },
          { type: "p", text: "Delivery management therefore has to be operational, not periodic: ownership and status need to be visible continuously, not reconstructed every fortnight." },
        ],
      },
      {
        heading: "What the discipline covers",
        blocks: [
          { type: "table", head: ["Concern", "Question it answers"], rows: [
            ["Programs & projects", "Which initiatives exist, their scope and their current status"],
            ["Suppliers & teams", "Who is responsible for which part of the delivery"],
            ["Access & documentation", "Which services and documents each team is allowed to use"],
            ["Integration progress", "Whether the integrations a project depends on actually work"],
            ["Delivery statistics", "How much has been delivered, and where work is stalled"],
          ] },
          { type: "diagram", caption: "From program to delivered integration", steps: ["Program", "Projects & suppliers", "Team roles & access", "Services & documentation", "Integration status"] },
          { type: "p", text: "The model is a chain. A program groups the projects; each project is assigned to suppliers and teams; members receive the services and documentation their project requires; and the integrations those teams build report status back into the same view." },
          { type: "p", text: "When access is tied to projects rather than granted on an ad hoc basis, a single structure addresses both governance and delivery by defining who can do what and tracking what has been completed. That link is described in [developer access management for integration projects](/resources/what-is-developer-access-management/)." },
        ],
      },
      {
        heading: "What to look for in an approach",
        blocks: [
          { type: "ul", items: [
            "A single operational view of programs, projects and suppliers, rather than a spreadsheet per supplier.",
            "Access that follows project membership, so onboarding and offboarding are not manual work.",
            "Integration status read from the platform itself, so progress does not depend on someone's report.",
            "Role-based controls that let team leaders manage their own members without a central bottleneck.",
            "Delivery statistics that managers can act on without asking each supplier for an update.",
          ] },
          { type: "p", text: "These capabilities assume that integrations are exposed through a governed layer. Without one, there is nothing for delivery management to read from. See [what an enterprise integration platform is](/resources/what-is-an-enterprise-integration-platform/) and [API orchestration](/resources/api-orchestration-explained/)." },
        ],
      },
    ],
    related: ["what-is-developer-access-management", "what-is-an-enterprise-integration-platform", "api-orchestration-explained"],
    product: {
      slug: "koala-flow",
      name: "Koala Flow",
      heading: "Integration Delivery with Koala Flow",
      text: "[Koala Flow](/products/koala-flow/) is Bane Tech's enterprise project & integration delivery management product, covering programs, projects, suppliers and teams. Explore how it brings delivery, access and integration status into one view.",
    },
  },
];

export const resourcesSeo = {
  title: "Resources: Integration, CDC, Observability & Architecture Guides | Bane Tech",
  description:
    "Technical guides on enterprise integration, CDC, observability, architecture and AI operational intelligence for software engineers and architects.",
};
